import { Effect, Option, Predicate, Schema, SchemaGetter } from 'effect';

// ── Shared enums ────────────────────────────────────────────

export const ConversionStatusSchema = Schema.Literals([
  'initializing',
  'processingPDF',
  'completedExtraction',
  'complete',
  'deleted',
  'Error',
]);

export const AudioUrlStatusSchema = Schema.Literals([
  'pending',
  'scheduled',
  'inProgress',
  'completed',
  'failed',
]);

export const SharingInfoVisibility = Schema.Literals([
  'private',
  'public',
  'selective_share',
]);

// Document type values returned in API responses. The /create endpoint accepts
// lowercase via `DocumentTypeSchema` (./document-type.ts); the response shape
// uses uppercase. Keeping the two schemas separate mirrors the legacy
// `AudioConversionSummaryDocumentTypeEnum`.
export const AudioConversionDocumentTypeSchema = Schema.Literals([
  'HTML',
  'PDF',
  'DOC',
  'DOCX',
  'PPT',
  'PPTX',
  'TXT',
  'MD',
  'RTF',
  'EPUB',
  'MOBI',
  'EMAIL',
  'EMAIL_ATTACHMENT',
]);

// Detected language of the document. 2-letter ISO 639-1 codes.
//
// This is the single source of truth for the set of supported languages
// across the whole repo. Add a new language HERE and everything else derives:
// `SUPPORTED_LANGUAGES` / `SUPPORTED_LANGUAGES_SET` below, the `VoiceDef.lang`
// constraint in ./voice, and `@listening/backend/kokoro` (which re-exports
// these). Do not maintain a parallel language list anywhere else.
//
// REVIEWERS: tightening the `language` response field from `Schema.String` to
// this enum is SAFE and was verified against prod — it does not 500 on legacy
// rows. The DB `audio.language` column is free `text` and holds legacy non-enum
// values, but every API read builds responses through `mapLanguage`
// (apps/api/src/api-impl/audio-endpoints.ts), which lowercases and maps anything
// not in this enum to `null` — so responses only ever emit a code here or null.
// We queried prod (2026-06): the 36 non-enum values are all UNSUPPORTED
// languages (german ~4.3k, dutch, russian, …); none is a supported language
// stored as a full name (no "english"/"spanish"/etc.), so nothing is lost and
// no row fails to encode. (`document_type` is likewise safe — it's a Postgres
// `pgEnum`, constrained at the DB.) See
// docs/projects/complete/web/query-stack-route-loaders.md ("Stricter response enums").
export const AudioConversionLanguageSchema = Schema.Literals([
  'en',
  'es',
  'fr',
  'hi',
  'it',
  'ja',
  'pt',
  'zh',
]);

export type SupportedLanguage = typeof AudioConversionLanguageSchema.Type;

export const SUPPORTED_LANGUAGES: ReadonlyArray<SupportedLanguage> =
  AudioConversionLanguageSchema.literals;

export const SUPPORTED_LANGUAGES_SET: ReadonlySet<SupportedLanguage> = new Set(
  AudioConversionLanguageSchema.literals,
);

// ── Tags & Sharing ──────────────────────────────────────────

export const AudioConversionTag = Schema.Struct({
  label: Schema.String,
  color: Schema.String,
});

export const SharedWith = Schema.Struct({
  emails: Schema.optional(Schema.NullOr(Schema.Array(Schema.String))).pipe(
    Schema.decodeTo(
      Schema.optional(Schema.toType(Schema.Array(Schema.String))),
      {
        decode: SchemaGetter.transformOptional(
          (value: Option.Option<readonly string[] | null | undefined>) =>
            value.pipe(Option.filter(Predicate.isNotNull)),
        ),
        encode: SchemaGetter.passthrough(),
      },
    ),
  ),
  user_ids: Schema.optional(Schema.NullOr(Schema.Array(Schema.String))).pipe(
    Schema.decodeTo(
      Schema.optional(Schema.toType(Schema.Array(Schema.String))),
      {
        decode: SchemaGetter.transformOptional(
          (value: Option.Option<readonly string[] | null | undefined>) =>
            value.pipe(Option.filter(Predicate.isNotNull)),
        ),
        encode: SchemaGetter.passthrough(),
      },
    ),
  ),
});

export const SharingInfo = Schema.Struct({
  visibility: Schema.optional(Schema.NullOr(SharingInfoVisibility)).pipe(
    Schema.decodeTo(Schema.UndefinedOr(Schema.toType(SharingInfoVisibility)), {
      decode: SchemaGetter.transformOptional((value) =>
        value.pipe(
          Option.filter(Predicate.isNotNull),
          Option.orElseSome(() => 'private' as const),
        ),
      ),
      encode: SchemaGetter.required(),
    }),
  ),
  unshareable_reason: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  shared_with: Schema.NullOr(SharedWith),
});

// ── Finalized metadata / Progress ───────────────────────────

export const TtsCacheVoicesData = Schema.Struct({
  voice: Schema.String,
  status: Schema.optional(
    Schema.NullOr(
      Schema.Literals(['pending', 'processing', 'completed', 'failed']),
    ),
  ).pipe(
    Schema.decodeTo(
      Schema.UndefinedOr(
        Schema.toType(
          Schema.Literals(['pending', 'processing', 'completed', 'failed']),
        ),
      ),
      {
        decode: SchemaGetter.transformOptional((value) =>
          value.pipe(
            Option.filter(Predicate.isNotNull),
            Option.orElseSome(() => 'pending' as const),
          ),
        ),
        encode: SchemaGetter.required(),
      },
    ),
  ),
  url: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  started_at_ms: Schema.Int,
  finished_at_ms: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  version: Schema.optional(Schema.NullOr(Schema.Int)).pipe(
    Schema.decodeTo(Schema.UndefinedOr(Schema.toType(Schema.Int)), {
      decode: SchemaGetter.transformOptional((value) =>
        value.pipe(
          Option.filter(Predicate.isNotNull),
          Option.orElseSome(() => 1413 as const),
        ),
      ),
      encode: SchemaGetter.required(),
    }),
  ),
});

export const AudioConversionFinalizedMetadata = Schema.Struct({
  chapter_details_s3_url: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  tts_finalized_details: Schema.optional(
    Schema.NullOr(Schema.Array(TtsCacheVoicesData)),
  ).pipe(
    Schema.decodeTo(
      Schema.optional(Schema.toType(Schema.Array(TtsCacheVoicesData))),
      {
        decode: SchemaGetter.transformOptional(
          (
            value: Option.Option<
              | ReadonlyArray<Schema.Schema.Type<typeof TtsCacheVoicesData>>
              | null
              | undefined
            >,
          ) => value.pipe(Option.filter(Predicate.isNotNull)),
        ),
        encode: SchemaGetter.passthrough(),
      },
    ),
  ),
});

export const AudioConversionProgress = Schema.Struct({
  chapter_id: Schema.NullOr(Schema.String),
  chapter_title: Schema.NullOr(Schema.String),
  estimated_percent_complete: Schema.Finite,
  timestamp_ms: Schema.Int,
  tts_batch_version: Schema.NullOr(Schema.Int),
  tts_id: Schema.NullOr(Schema.String),
  doc_pack_page: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_start: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_end: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  updated_at_ms: Schema.NullOr(Schema.Int),
});

// ── Doc-pack ────────────────────────────────────────────────

export const DocPackInfo = Schema.Struct({
  url: Schema.String,
  content_version: Schema.Int,
});

export type DocPackInfo = Schema.Schema.Type<typeof DocPackInfo>;

// ── AudioConversionSummary ──────────────────────────────────

export const AudioConversionSummary = Schema.Struct({
  id: Schema.String,
  legacy_id: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  archived_at_ms: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  conversion_status: ConversionStatusSchema,
  cover_img_url: Schema.NullOr(Schema.String),
  created_at_ms: Schema.Int,
  document_type: Schema.NullOr(AudioConversionDocumentTypeSchema).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  document_url: Schema.NullOr(Schema.String),
  doc_pack: Schema.NullOr(DocPackInfo).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  error: Schema.NullOr(Schema.String),
  finalized_metadata: Schema.NullOr(AudioConversionFinalizedMetadata).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  folder_id: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  folder_path: Schema.Array(Schema.String),
  language: Schema.NullOr(AudioConversionLanguageSchema),
  owner_id: Schema.NullOr(Schema.String),
  page_count: Schema.NullOr(Schema.Int),
  progress: Schema.NullOr(AudioConversionProgress),
  sharing: Schema.optional(Schema.NullOr(SharingInfo)).pipe(
    Schema.decodeTo(Schema.optional(Schema.toType(SharingInfo)), {
      decode: SchemaGetter.transformOptional(
        (
          value: Option.Option<
            Schema.Schema.Type<typeof SharingInfo> | null | undefined
          >,
        ) => value.pipe(Option.filter(Predicate.isNotNull)),
      ),
      encode: SchemaGetter.passthrough(),
    }),
  ),
  tags: Schema.Array(AudioConversionTag),
  title: Schema.NullOr(Schema.String),
  url_slug: Schema.NullOr(Schema.String),
  publishing_author: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  publishing_year: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  description: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  category: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  // Which conversion pipeline produced this audio. Null for older/untracked rows. Surfaced
  // (dev/staging only) in the web app reader + library.
  // @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
  conversion_pipeline_version: Schema.NullOr(
    Schema.Literals(['v1', 'v2']),
  ).pipe(Schema.withDecodingDefaultType(Effect.succeed(null))),
});

// ── Chapter types ───────────────────────────────────────────

export const ChapterTimestampMs = Schema.Struct({
  start: Schema.Int,
  end: Schema.Int,
});

export const ChapterBbox = Schema.Struct({
  page: Schema.Int,
  bbox: Schema.Tuple([
    Schema.Finite,
    Schema.Finite,
    Schema.Finite,
    Schema.Finite,
  ]),
});

export const ChapterWord = Schema.Struct({
  text: Schema.String,
  bboxes: Schema.Array(ChapterBbox),
  timestamp_ms: Schema.NullOr(ChapterTimestampMs),
  tts_hash_id: Schema.NullOr(Schema.String),
});

export const ChapterSentence = Schema.Struct({
  words: Schema.Array(ChapterWord),
  bboxes: Schema.Array(ChapterBbox),
  timestamp_ms: Schema.NullOr(ChapterTimestampMs),
});

export const ChapterParagraph = Schema.Struct({
  sentences: Schema.Array(ChapterSentence),
  bboxes: Schema.Array(ChapterBbox),
  timestamp_ms: Schema.NullOr(ChapterTimestampMs),
});

export const ChapterSummary = Schema.Struct({
  id: Schema.String,
  legacy_id: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  title: Schema.String,
  index: Schema.Int,
  audio_url: Schema.NullOr(Schema.String),
  audio_url_status: AudioUrlStatusSchema,
  avg_estimated_tts_chunk_duration_ms: Schema.Finite,
  avg_paragraph_word_length: Schema.Finite,
  bbox_pages: Schema.Array(Schema.Int),
  num_paragraphs: Schema.Int,
  tts_batch_version: Schema.Int,
  tts_ids: Schema.Array(Schema.String).annotate({
    description:
      'DEPRECATED: legacy stored TTS chunk IDs. New readers should key playback, progress, notes, and highlights by doc-pack page/char ranges.',
    jsonSchema: { deprecated: true },
  }),
});

export type ChapterSummary = Schema.Schema.Type<typeof ChapterSummary>;

export const ChapterDetail = Schema.Struct({
  id: Schema.String,
  legacy_id: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  title: Schema.String,
  index: Schema.Int,
  audio_url: Schema.NullOr(Schema.String),
  audio_url_status: AudioUrlStatusSchema,
  avg_estimated_tts_chunk_duration_ms: Schema.Finite,
  avg_paragraph_word_length: Schema.Finite,
  bbox_pages: Schema.Array(Schema.Int),
  num_paragraphs: Schema.Int,
  paragraphs: Schema.Array(ChapterParagraph),
  timestamp_ms: Schema.NullOr(ChapterTimestampMs),
  tts_batch_version: Schema.Int,
  tts_ids: Schema.Array(Schema.String).annotate({
    description:
      'DEPRECATED: legacy stored TTS chunk IDs. New readers should key playback, progress, notes, and highlights by doc-pack page/char ranges.',
    jsonSchema: { deprecated: true },
  }),
});

// ── Finalized S3 archive schemas ─────────────────────────────
// Schemas for the pre-built JSON archives uploaded to S3 during finalization.
// The mobile app downloads these for offline use (file_service.dart).

/** TTS detail as serialized into the finalized tts-details.json S3 archive. */
export const FinalizedTtsDetail = Schema.Struct({
  id: Schema.String,
  text: Schema.String,
  url: Schema.NullOr(Schema.String),
  format: Schema.Literal('mp3'),
  duration_ms: Schema.Int,
  /**
   * @deprecated TTS audio URLs are permanent public S3 URLs, not signed/expiring
   * URLs. The backend always sets this to a far-future constant (9999999999999)
   * and no client consults it to drive expiry/refetch logic. Kept for response
   * shape compatibility; do not rely on it.
   */
  url_expires_at_ms: Schema.Int.annotate({
    description:
      'DEPRECATED: always a far-future constant; URLs are permanent public S3 URLs, not expiring. Do not rely on this field.',
    jsonSchema: { type: 'number', deprecated: true },
  }),
  alignment_timestamps: Schema.Array(ChapterTimestampMs),
  voice_data_version: Schema.Int,
  voice: Schema.String,
});

// ── AudioConversionDetail ───────────────────────────────────

export const AudioConversionDetail = AudioConversionSummary.pipe(
  Schema.fieldsAssign({
    summary: Schema.optional(Schema.NullOr(Schema.String)),
    key_points: Schema.optional(Schema.NullOr(Schema.String)),
    chapters: Schema.Array(ChapterSummary),
    voice: Schema.String,
  }),
);

export type AudioConversionDetail = Schema.Schema.Type<
  typeof AudioConversionDetail
>;

// ── Response DTOs ───────────────────────────────────────────

export const GetListAudioV2200 = Schema.Struct({
  audio_conversions: Schema.Array(AudioConversionSummary),
  cursor: Schema.NullOr(Schema.String),
  tts_finalized_version: Schema.Int,
  voice_data_version: Schema.Int,
});

export const GetGetAudioV2200 = Schema.Struct({
  audio_conversion: AudioConversionSummary,
});

export const GetGetAudioDetailedV2200 = Schema.Struct({
  audio_conversion: AudioConversionDetail,
});

// Dev/staging-only conversion diagnostics — one row per metered pipeline step
// (from the `conversion_cost` table). See web-app-tooling.md §6.
export const ConversionDiagnosticsStep = Schema.Struct({
  service: Schema.String,
  engine: Schema.NullOr(Schema.String),
  duration_ms: Schema.NullOr(Schema.Int),
  estimated_cost_microdollars: Schema.Finite,
  usage_metrics: Schema.NullOr(Schema.Record(Schema.String, Schema.Unknown)),
  created_at_ms: Schema.Int,
});

export const GetConversionDiagnosticsV2200 = Schema.Struct({
  conversion_pipeline_version: Schema.NullOr(Schema.Literals(['v1', 'v2'])),
  steps: Schema.Array(ConversionDiagnosticsStep),
});

// Dev/staging-only: the resolved server-side conversion config defaults — what a conversion
// gets when an upload sends no override. Shown under each "Server default" option on the
// conversion settings page. See web-app-tooling.md §3.
export const GetConversionDefaultsV2200 = Schema.Struct({
  pipeline_version: Schema.Literals(['v1', 'v2']),
  // v2 only — v1 always runs vision regardless of this value.
  vision_v2_enabled: Schema.Boolean,
  ocr_provider: Schema.Literals(['azure', 'paddle-modal']),
  // Paddle sub-model set; only meaningful when ocr_provider is 'paddle-modal'.
  paddle_mode: Schema.Literals(['minimal', 'full']),
  // OpenRouter model id the v2 label step runs on; null ⇒ the fine-tuned 8B.
  label_general_model: Schema.NullOr(Schema.String),
  // What the v2 update step runs when no override is sent.
  update_mode: Schema.Literals(['finetune', 'general', 'deterministic']),
  word_enrichment_enabled: Schema.Boolean,
});

export const PostCreateAudioV2200 = Schema.Struct({
  audio_conversion: AudioConversionSummary,
});

export const GetListAudioChaptersV2200 = Schema.Struct({
  chapters: Schema.Array(ChapterDetail),
  cursor: Schema.NullOr(Schema.String),
});

export const GetGetChapterV2200 = Schema.Struct({
  chapter: ChapterDetail,
});
